import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex, utf8ToBytes } from "@noble/hashes/utils.js";
import { z } from "zod";
/*
 * Desk's tools, called by the Twin's task agent mid-turn (docs/specs/twin-desk-client-tools.md).
 *
 * The manifest is held here, on the server's side, never sent by a client: each toolset's tools, what they take, and
 * whether they read or act on the person's Mac. `desk_call`'s parameters are generated from it, and its reviewed hash
 * (the SHA-256 of the toolset's canonical JSON) sits beside it: changing a tool without changing the hash fails
 * `desk-client-tools.test.ts`, which is the review step. Calls are validated against it three times: by `desk_call` in
 * the executor, by the tenant when it records them, and by Desk before it runs them.
 *
 * Things on the Mac are named only by the session's own handles (`h1`, `h2`), which Desk keeps with what they name:
 * no Mac id ever reaches the Twin.
 *
 * Each tool is an MCP tool definition (name, description, inputSchema, annotations), as the executor's plugin tools
 * are, so a later move to MCP's own request-and-answer pattern stays cheap. The transport is not MCP: Desk claims and
 * answers calls outbound through the public gateway, which works from behind NAT with the gateway's auth, audit and
 * tenant checks, where an MCP server on the Mac would not be reachable from the executor.
 */
export const DESK_CALL_TOOL_NAME = "desk_call";
export const DESK_CLIENT_CAPABILITY = "desk-client";
export const DESK_CLIENT_TOOLSETS = ["desk-v1"];
export const deskClientToolsetSchema = z.enum(DESK_CLIENT_TOOLSETS);
/** Caps (review L3): calls a batch, input and result sizes, batches a turn and a session an hour, and how long a call waits. */
export const DESK_CALL_LIMITS = {
    callsPerBatch: 5,
    inputBytes: 4_096,
    resultBytes: 16_384,
    batchResultBytes: 49_152,
    batchesPerRun: 12,
    batchesPerSessionHour: 60,
    expiresAfterMs: 90_000,
    waitRequestMs: 20_000,
    signatureSkewMs: 120_000,
    retentionMs: 30 * 24 * 60 * 60 * 1000,
};
const handle = { type: "string", pattern: "^h[0-9]{1,4}$", description: "A handle the session was given: desk:h1 is \"h1\"." };
const reads = (title) => ({ title, readOnlyHint: true, destructiveHint: false, idempotentHint: true, openWorldHint: false });
const acts = (title) => ({ title, readOnlyHint: false, destructiveHint: false, idempotentHint: false, openWorldHint: false });
/** `desk-v1`: what the Twin's agent may ask of Desk. Never anything that sends, deletes, archives or unsubscribes. */
export const DESK_CLIENT_TOOLSET_V1 = [
    {
        name: "mail.search",
        description: "Search the person's mail kept on their Mac. Gives each conversation found with its subject, people, date and a snippet, and a new handle to read, open or reply to it by. Without exact it is a ranked search: any of the words may match and a slip is forgiven. With exact, only conversations holding the query word for word, in order, are found, and none is a faithful answer that nothing holds it.",
        annotations: reads("Search the mail on the Mac"),
        inputSchema: {
            type: "object", additionalProperties: false, required: ["query"],
            properties: {
                query: { type: "string", minLength: 1, maxLength: 200, description: "Words, names or addresses, as a mail search takes them." },
                most: { type: "integer", minimum: 1, maximum: 20, description: "How many conversations at most: 10 when left out." },
                exact: { type: "boolean", description: "The query as one exact phrase (case, accents and punctuation aside), never a looser search: for an exact phrase, a code or a quote the person asked for word for word." },
            },
        },
    },
    {
        name: "mail.read",
        description: "Read one conversation from the person's mail on their Mac, by its handle: who wrote and what each message says, newest last. Lines holding a code, a password or a key are left out.",
        annotations: reads("Read an email on the Mac"),
        inputSchema: { type: "object", additionalProperties: false, required: ["handle"], properties: { handle } },
    },
    {
        name: "calendar.read",
        description: "Read the person's calendar on their Mac between two times: each event's title, time and people, with a handle to open it by.",
        annotations: reads("Read the calendar on the Mac"),
        inputSchema: {
            type: "object", additionalProperties: false, required: ["from", "to"],
            properties: {
                from: { type: "string", format: "date-time", maxLength: 40 },
                to: { type: "string", format: "date-time", maxLength: 40 },
            },
        },
    },
    {
        name: "tabs.list",
        description: "List the tabs open in Desk: each one's title and address without its query, with a handle to open it by. Private tabs are never listed.",
        annotations: reads("List the open tabs"),
        inputSchema: { type: "object", additionalProperties: false, properties: {} },
    },
    {
        name: "desk.open",
        description: "Open, on the person's Mac, the thing a handle names (an email, a draft, an event, a tab), or a page of the organisation's app by its path (/workspace/...), as a double-click opens it.",
        annotations: acts("Open on the Mac"),
        inputSchema: {
            type: "object", additionalProperties: false,
            properties: {
                handle,
                path: { type: "string", pattern: "^/(workspace|inbox)/[A-Za-z0-9/_.~-]*(\\?[A-Za-z0-9=&_.~%-]*)?$", maxLength: 2_000,
                    description: "A page of the organisation's app, as its tools give it: /workspace/channels/c1?record=r1." },
            },
            oneOf: [{ required: ["handle"] }, { required: ["path"] }],
        },
    },
    {
        name: "tabs.open",
        description: "Open an https address in a tab on the person's Mac. An address you wrote waits for the person's click before it opens.",
        annotations: acts("Open an address in a tab"),
        inputSchema: {
            type: "object", additionalProperties: false, required: ["address"],
            properties: { address: { type: "string", pattern: "^https://", maxLength: 2_000 } },
        },
    },
    {
        name: "mail.draft",
        description: "Write a reply into a draft in a conversation on the person's Mac, by the conversation's handle, to whom it is with (all: to everyone in it). The person sends it themselves; nothing is ever sent. If they have started a reply there, their words stay and these go after them; Undo takes these out.",
        annotations: acts("Write a reply into a draft"),
        inputSchema: {
            type: "object", additionalProperties: false, required: ["handle", "words"],
            properties: {
                handle,
                words: { type: "string", minLength: 1, maxLength: 8_000, description: "The reply itself, as the person would write it, without a signature." },
                all: { type: "boolean", description: "Reply to everyone in the conversation." },
            },
        },
    },
];
export const DESK_CLIENT_TOOLSETS_BY_NAME = {
    "desk-v1": DESK_CLIENT_TOOLSET_V1,
};
/** Each toolset's reviewed hash: change it only with a review of what the toolset lets the Twin do on the Mac. */
export const DESK_CLIENT_TOOLSET_REVIEWED_SHA256 = {
    // 21 Oct 2026: mail.search gains `exact`, a read narrower than the ranked search it already was (audit F5).
    "desk-v1": "ee938ec0a04006eb5ee5c211a86aed4978569aefc35bcdfc5fc4b4edf32244bd",
};
/** JSON with its keys sorted, for hashing. */
export function deskClientCanonicalJson(value) {
    if (Array.isArray(value))
        return `[${value.map(deskClientCanonicalJson).join(",")}]`;
    if (value && typeof value === "object") {
        return `{${Object.entries(value)
            .filter(([, nested]) => nested !== undefined)
            .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
            .map(([key, nested]) => `${JSON.stringify(key)}:${deskClientCanonicalJson(nested)}`)
            .join(",")}}`;
    }
    return JSON.stringify(value) ?? "null";
}
export function deskClientSha256Hex(text) {
    return bytesToHex(sha256(utf8ToBytes(text)));
}
/** A toolset's hash as it stands. */
export function deskClientToolsetSha256(toolset) {
    return deskClientSha256Hex(deskClientCanonicalJson(DESK_CLIENT_TOOLSETS_BY_NAME[toolset]));
}
/** Whether a toolset is known and as reviewed: an unknown, retired or changed one gets no `desk_call`. */
export function deskClientToolsetReviewed(toolset) {
    const parsed = deskClientToolsetSchema.safeParse(toolset);
    return parsed.success && deskClientToolsetSha256(parsed.data) === DESK_CLIENT_TOOLSET_REVIEWED_SHA256[parsed.data];
}
/** `desk_call`'s parameters for the model, generated from a toolset: one branch per tool, its name constant. */
export function deskCallParameters(toolset) {
    return {
        type: "object",
        additionalProperties: false,
        required: ["calls"],
        properties: {
            calls: {
                type: "array",
                minItems: 1,
                maxItems: DESK_CALL_LIMITS.callsPerBatch,
                items: {
                    oneOf: DESK_CLIENT_TOOLSETS_BY_NAME[toolset].map((tool) => ({
                        type: "object",
                        additionalProperties: false,
                        required: ["tool", "input"],
                        properties: { tool: { const: tool.name, description: tool.description }, input: tool.inputSchema },
                    })),
                },
            },
        },
    };
}
/** A page of the organisation's app desk.open may open: its path, and a plain query (`?record=`), nothing else. */
export const DESK_APP_PATH = /^\/(workspace|inbox)\/[A-Za-z0-9/_.~-]*(\?[A-Za-z0-9=&_.~%-]*)?$/u;
// Each tool's input, as the server and Desk check it.
const handleSchema = z.string().regex(/^h[0-9]{1,4}$/u);
const isoTimeSchema = z.string().max(40).refine((value) => !Number.isNaN(Date.parse(value)), "an ISO 8601 time");
const deskCallInputSchemas = {
    "mail.search": z.object({
        query: z.string().min(1).max(200), most: z.number().int().min(1).max(20).optional(), exact: z.boolean().optional(),
    }).strict(),
    "mail.read": z.object({ handle: handleSchema }).strict(),
    "calendar.read": z.object({ from: isoTimeSchema, to: isoTimeSchema }).strict(),
    "tabs.list": z.object({}).strict(),
    "desk.open": z.union([
        z.object({ handle: handleSchema }).strict(),
        z.object({ path: z.string().max(2_000).regex(DESK_APP_PATH) }).strict(),
    ]),
    "tabs.open": z.object({ address: z.string().max(2_000).regex(/^https:\/\//u).url() }).strict(),
    "mail.draft": z.object({ handle: handleSchema, words: z.string().min(1).max(8_000), all: z.boolean().optional() }).strict(),
};
export const deskCallSchema = z.object({
    tool: z.string().min(1).max(40),
    input: z.record(z.string(), z.unknown()),
}).strict();
export const deskCallArgsSchema = z.object({
    calls: z.array(deskCallSchema).min(1).max(DESK_CALL_LIMITS.callsPerBatch),
}).strict();
/** Why a batch can't go to Desk, or nothing when every call is in the toolset, well formed and within its caps. */
export function deskCallProblem(toolset, args) {
    const parsed = deskCallArgsSchema.safeParse(args);
    if (!parsed.success)
        return `desk_call takes {calls: [{tool, input}]}, 1 to ${DESK_CALL_LIMITS.callsPerBatch} calls`;
    const tools = new Set(DESK_CLIENT_TOOLSETS_BY_NAME[toolset].map((tool) => tool.name));
    for (const [index, call] of parsed.data.calls.entries()) {
        if (!tools.has(call.tool))
            return `call ${index + 1}: ${call.tool} is not one of Desk's tools`;
        if (utf8ToBytes(JSON.stringify(call.input)).length > DESK_CALL_LIMITS.inputBytes) {
            return `call ${index + 1}: its input is over ${DESK_CALL_LIMITS.inputBytes} bytes`;
        }
        const schema = deskCallInputSchemas[call.tool];
        const input = schema.safeParse(call.input);
        if (!input.success)
            return `call ${index + 1}: ${call.tool}'s input is not what it takes`;
    }
    return null;
}
/** Whether a tool of a toolset acts on the Mac, rather than only reading. */
export function deskCallActs(toolset, tool) {
    return DESK_CLIENT_TOOLSETS_BY_NAME[toolset].some((entry) => entry.name === tool && !entry.annotations.readOnlyHint);
}
// MARK: - signatures
/*
 * Desk signs with a P-256 key in the Mac's Secure Enclave, ECDSA over SHA-256 of a canonical string: never the request
 * body, which the public gateway re-serialises. A signature is accepted within `signatureSkewMs` of the tenant's clock,
 * and only with a `signedAt` later than the last the session accepted.
 */
export const DESK_CLIENT_SIGNATURE_PREFIX = "mlx-desk-client/v1";
const base64url = z.string().regex(/^[A-Za-z0-9_-]+$/u);
/** A 65-byte uncompressed P-256 point, base64url. */
export const deskClientDeviceKeySchema = base64url.min(86).max(88);
/** A 64-byte r‖s signature, base64url. */
export const deskClientSignatureSchema = base64url.min(85).max(88);
export const deskClientSignedAtSchema = z.number().int().positive();
export const deskClientSignedSchema = z.object({
    signedAt: deskClientSignedAtSchema,
    signature: deskClientSignatureSchema,
}).strict();
/** `twin.query.create`'s `deskClient`: binds the new session to this Mac's Desk. */
export const deskClientBindingSchema = deskClientSignedSchema.extend({
    toolset: deskClientToolsetSchema,
    deviceKey: deskClientDeviceKeySchema,
}).strict();
export function deskClientBindString(input) {
    return [DESK_CLIENT_SIGNATURE_PREFIX, "bind", input.organisationId, deskClientSha256Hex(input.question), String(input.signedAt)].join("\n");
}
export function deskClientFollowString(input) {
    return [DESK_CLIENT_SIGNATURE_PREFIX, "follow", input.sessionId, deskClientSha256Hex(input.message), String(input.signedAt)].join("\n");
}
export function deskClientClaimString(input) {
    return [DESK_CLIENT_SIGNATURE_PREFIX, "claim", input.sessionId, String(input.signedAt)].join("\n");
}
/** An answer's signed string: each result's call, outcome, whether it was cut or addressed to an AI, and content hash. */
export function deskClientAnswerString(input) {
    return [
        DESK_CLIENT_SIGNATURE_PREFIX, "answer", input.sessionId, String(input.signedAt),
        ...input.results.map((result) => [result.callId, result.outcome, `cut=${result.cut ? 1 : 0}`,
            `addressed=${result.addressed ? 1 : 0}`, deskClientSha256Hex(result.content ?? "")].join(" ")),
    ].join("\n");
}
// MARK: - public operations: Desk claims and answers its session's calls
/**
 * What Desk made of a call: `done`; `declined`, the person's own "no"; `unanswered`, a change that waited on the person's
 * click past its time (they haven't said no); `refused`, outside what Desk does; `failed`, with why.
 */
export const deskCallOutcomeSchema = z.enum(["done", "declined", "unanswered", "refused", "failed"]);
export const twinDeskCallsClaimInputSchema = deskClientSignedSchema;
export const twinDeskCallSchema = z.object({
    id: z.string().min(1).max(80),
    tool: z.string().min(1).max(40),
    input: z.record(z.string(), z.unknown()),
    /** Its turn's tool call, so calls made together run together. */
    batchId: z.string().min(1).max(200),
    position: z.number().int().nonnegative(),
    expiresAt: z.number().int().positive(),
}).strict();
export const twinDeskCallsClaimResponseSchema = z.object({
    toolset: deskClientToolsetSchema,
    calls: z.array(twinDeskCallSchema).max(DESK_CALL_LIMITS.callsPerBatch * 4),
}).strict();
export const twinDeskCallResultSchema = z.object({
    callId: z.string().min(1).max(80),
    outcome: deskCallOutcomeSchema,
    /** What it gave, for `done`; why, in plain words, for `failed`. Nothing for `declined`, `unanswered` or `refused`. */
    content: z.string().max(DESK_CALL_LIMITS.resultBytes).optional(),
    /** Cut to fit: never cut silently. */
    cut: z.boolean().optional(),
    /** Its words were addressed to an AI (a page or an email talking to the model). */
    addressed: z.boolean().optional(),
}).strict();
export const twinDeskCallsAnswerInputSchema = deskClientSignedSchema.extend({
    results: z.array(twinDeskCallResultSchema).min(1).max(DESK_CALL_LIMITS.callsPerBatch),
}).strict();
export const twinDeskCallsAnswerResponseSchema = z.object({
    /** The calls this answer settled; one already answered or expired isn't counted again. */
    accepted: z.number().int().nonnegative(),
}).strict();
// MARK: - worker routes: the executor records a batch and waits for its results
const workerLeaseSchema = z.object({
    runId: z.string().min(1),
    tenantSlug: z.string().min(1),
    workerId: z.string().min(1),
    attemptNumber: z.number().int().positive(),
}).strict();
export const workerDeskCallsCreateInputSchema = workerLeaseSchema.extend({
    toolCallId: z.string().min(1).max(200),
    toolset: deskClientToolsetSchema,
    calls: z.array(deskCallSchema).min(1).max(DESK_CALL_LIMITS.callsPerBatch),
}).strict();
export const workerDeskCallsCreateResponseSchema = z.object({
    batchId: z.string().min(1),
    callIds: z.array(z.string().min(1)).min(1).max(DESK_CALL_LIMITS.callsPerBatch),
    expiresAt: z.number().int().positive(),
}).strict();
export const workerDeskCallsWaitInputSchema = workerLeaseSchema.extend({
    batchId: z.string().min(1),
    /** How long the tenant may hold this request waiting for a result: the executor's own timeout allows a little more. */
    holdMs: z.number().int().min(1_000).max(DESK_CALL_LIMITS.waitRequestMs),
}).strict();
export const workerDeskCallSettledSchema = z.object({
    callId: z.string().min(1),
    tool: z.string().min(1),
    status: z.enum(["answered", "expired"]),
    outcome: deskCallOutcomeSchema.optional(),
    content: z.string().optional(),
    cut: z.boolean().optional(),
    addressed: z.boolean().optional(),
}).strict();
export const workerDeskCallsWaitResponseSchema = z.object({
    /** `settled` once every call of the batch is answered or expired; else `waiting`, to ask again. */
    status: z.enum(["waiting", "settled"]),
    calls: z.array(workerDeskCallSettledSchema).max(DESK_CALL_LIMITS.callsPerBatch),
}).strict();
/**
 * What the claim gives a run whose requester's Desk is bound to its session (docs/specs/twin-desk-client-tools.md
 * §2.2): the toolset, and the binding's owner, whom the executor checks is the run's own requester before it offers
 * desk_call.
 */
export const claimedDeskClientSchema = z.object({ toolset: deskClientToolsetSchema, owner: z.string().min(1) }).strict();
